import React from 'react';
import { cn } from '@/lib/utils';
/**
 * Renders a phone-frame mockup with an iframe pointing at the public
 * `/page-designs/preview` route. The preview controller renders synthetic
 * sample content using the chosen design + pattern + mode, and is the same
 * surface the in-app theme picker uses — so what marketing visitors see is
 * exactly what they'd ship.
 *
 * Lazy-loads (`loading="lazy"`) so a page with 4–6 of these doesn't fetch
 * everything upfront. The route is unauthenticated and never records a
 * `ScanRecord`, so visiting marketing pages doesn't pollute campaign
 * analytics.
 */
export function CampaignPagePreview({ design, pattern, mode, caption, eyebrow, size = 'md', className, }) {
    const previewUrl = React.useMemo(() => {
        const params = new URLSearchParams({ design, mode });
        if (pattern)
            params.set('pattern', pattern);
        return `/page-designs/preview?${params.toString()}`;
    }, [design, pattern, mode]);
    const phoneSize = size === 'sm' ? 'aspect-[9/19] w-full max-w-[180px]' : 'aspect-[9/19] w-full max-w-[260px]';
    return (<figure className={cn('flex flex-col items-center gap-3', className)}>
            <div className={cn('relative overflow-hidden rounded-[2.4rem] border-[6px] border-neutral-900 bg-neutral-900 shadow-2xl', 'dark:border-neutral-700', phoneSize)}>
                {/* Notch — purely cosmetic, hides 18px of the iframe top */}
                <div className="absolute left-1/2 top-1.5 z-10 h-3.5 w-16 -translate-x-1/2 rounded-full bg-neutral-900"/>
                <iframe title={`${design} preview`} src={previewUrl} sandbox="allow-scripts allow-same-origin" loading="lazy" className="h-full w-full border-0 bg-background"/>
            </div>
            <figcaption className="text-center">
                {eyebrow && (<div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {eyebrow}
                    </div>)}
                <div className={cn('font-medium', size === 'sm' ? 'text-xs' : 'text-sm')}>{caption}</div>
            </figcaption>
        </figure>);
}
